import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';

// Inquiry-flow KYC. TheSolar hosts the verification page (Persona); we never
// collect identity data (SSN/DOB/address) in-app. The backend surfaces a
// hosted `kycLink` when the player must verify — from /player/kyc_validate, or
// from a deposit/withdraw attempt (code VERIFY_KYC) — and we just open it. The
// hosted page + webhook update the status asynchronously.
type KycStatus = 'verified' | 'completed' | 'in_progress' | 'required' | 'declined' | 'not_applicable' | 'unknown';

interface StatusResponse { kycStatus: KycStatus; requiredTemplate: string | null; transactionId: string | null }

interface KycValue {
  status: KycStatus;
  verified: boolean;
  /** Identity not yet verified AND KYC applies to this brand — show the red dot / gate withdrawals. */
  mustVerify: boolean;
  kycLink: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  openKyc: () => void;
}

// Pull a hosted KYC link out of a failed deposit/withdraw response. Inquiry-flow
// brands return { code: 'VERIFY_KYC', kycLink } when verification is required.
export const kycLinkFromError = (err: any): string | null => {
  const d = err?.response?.data;
  return d?.code === 'VERIFY_KYC' ? (d.kycLink ?? null) : null;
};

const KycContext = createContext<KycValue | null>(null);
export const useKyc = () => {
  const v = useContext(KycContext);
  if (!v) throw new Error('useKyc must be used within KycProvider');
  return v;
};

export function KycProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [status, setStatus] = useState<KycStatus>('unknown');
  const [kycLink, setKycLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) { setStatus('unknown'); setKycLink(null); setLoading(false); return; }
    try {
      const res = await api.get<StatusResponse>('/orders/topup/thesolar/kyc');
      setStatus(res.data.kycStatus || 'unknown');
      setKycLink(null);
    } catch (e: any) {
      // Inquiry-flow brands return VERIFY_KYC + a hosted kyc_link here when the
      // player isn't verified. Anything else: fail open (never block the app).
      const link = kycLinkFromError(e);
      if (link !== null || e?.response?.data?.code === 'VERIFY_KYC') { setStatus('required'); setKycLink(link); }
      else setStatus('unknown');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => { refresh(); }, [refresh]);

  const verified = status === 'verified' || status === 'completed';
  const applicable = status !== 'not_applicable' && status !== 'unknown';
  const mustVerify = applicable && !verified;

  // Open TheSolar's hosted page. Called from a click gesture so the popup isn't
  // blocked. If we don't have a link yet (e.g. player has no Solar account until
  // their first deposit), re-read status — it'll arrive with the next attempt.
  // ponytail: no pre-emptive link fetch; the deposit/withdraw flow surfaces it.
  const openKyc = () => {
    if (kycLink) window.open(kycLink, '_blank', 'noopener');
    else refresh();
  };

  return (
    <KycContext.Provider value={{ status, verified, mustVerify, kycLink, loading, refresh, openKyc }}>
      {children}
    </KycContext.Provider>
  );
}
